import React, { useCallback, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FaFileDownload, FaPrint } from "react-icons/fa";

import { formatFieldAppDateTime } from "./fieldAppTimezoneDisplay";
import { buildMachineOperationMap } from "./fieldAppMachineUtils";
import FieldAppTelemetryMapPanel from "./FieldAppTelemetryMapPanel";
import {
  buildTelemetryScheduleFileName,
  exportTelemetrySchedulePdf,
  printTelemetrySchedule,
} from "./fieldAppTelemetryScheduleExport";

import "./FieldAppTelemetrySchedulePanel.css";
import "./FieldAppTelemetryMapPanel.css";

function formatHours(value) {
  const hours = Number(value);
  if (!Number.isFinite(hours) || hours <= 0) {
    return "—";
  }
  return `${hours.toFixed(2)} h`;
}

function formatSpeed(value) {
  const speed = Number(value);
  if (!Number.isFinite(speed) || speed <= 0) {
    return "—";
  }
  return `${speed.toFixed(1)} km/h`;
}

function formatStoppedMinutes(value) {
  const minutes = Number(value);
  if (!Number.isFinite(minutes) || minutes <= 0) {
    return "—";
  }
  return `${minutes} min`;
}

function formatStoppedPercent(value) {
  const percent = Number(value);
  if (!Number.isFinite(percent) || percent < 0) {
    return "—";
  }
  return `${percent.toFixed(1)}%`;
}

function formatDayLabel(dateIso, compact) {
  if (!dateIso) return "—";
  const date = new Date(`${dateIso}T12:00:00`);
  if (Number.isNaN(date.getTime())) return dateIso;
  if (compact) {
    return date.toLocaleDateString(undefined, {
      day: "2-digit",
      month: "2-digit",
    });
  }
  return date.toLocaleDateString(undefined, {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
  });
}

function formatReportGeneratedAt() {
  return new Date().toLocaleString(undefined, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function openMapView(setMapView, payload) {
  setMapView({
    ...payload,
    token: Date.now(),
  });
}

function mapCenterFromPoint(point) {
  if (!point) return null;
  const lat = Number(point.lat);
  const lng = Number(point.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) < 0.000001 && Math.abs(lng) < 0.000001) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng, time: point.iso || point.time || point.focusStart || null };
}

function ScheduleTimeCell({
  cell,
  dayKey,
  label,
  timezoneMode,
  onOpenMap,
  t,
  printable,
}) {
  if (!cell || !cell.iso) {
    return <span className="field-app-telemetry-schedule__empty">—</span>;
  }

  const timeLabel = formatFieldAppDateTime(cell.iso, timezoneMode, "hm");
  const canOpen = !printable && cell.focusStart && cell.focusEnd && onOpenMap;

  if (!canOpen) {
    return <span>{timeLabel}</span>;
  }

  return (
    <button
      type="button"
      className="field-app-telemetry-schedule__time-btn"
      title={t("Abrir mapa neste trecho")}
      onClick={() =>
        onOpenMap({
          dayKey: dayKey || String(cell.iso).slice(0, 10),
          focusStart: cell.focusStart,
          focusEnd: cell.focusEnd,
          label,
          mapCenter: mapCenterFromPoint(cell),
          mapFullDay: true,
        })
      }
    >
      {timeLabel}
    </button>
  );
}

function MaxSpeedCell({ row, onOpenMap, t, printable }) {
  const point = row.maxSpeedAt;
  const label = formatSpeed(row.maxSpeedKmh);
  if (!point || !point.focusStart || !point.focusEnd || !onOpenMap || printable) {
    return <span>{label}</span>;
  }

  return (
    <button
      type="button"
      className="field-app-telemetry-schedule__time-btn"
      title={t("Abrir mapa no ponto de velocidade máxima")}
      onClick={() =>
        onOpenMap({
          dayKey: row.date,
          focusStart: point.focusStart,
          focusEnd: point.focusEnd,
          label: t("Velocidade máxima ({{speed}})", { speed: label }),
          mapCenter: mapCenterFromPoint(point),
          mapFullDay: true,
        })
      }
    >
      {label}
    </button>
  );
}

function buildOperationReturnSearch(farmId, dayKey) {
  const params = new URLSearchParams({
    return_to: `/${farmId}/field-app/?tab=telemetry`,
  });
  if (dayKey) {
    params.set("telemetry_day", dayKey);
  }
  return `?${params.toString()}`;
}

function DayMachinesCell({
  machines,
  operationMap,
  farmId,
  dayKey,
  printable,
  t,
}) {
  const list = Array.isArray(machines) ? machines.filter(Boolean) : [];
  if (!list.length) {
    return <span className="field-app-telemetry-schedule__empty">—</span>;
  }

  if (printable) {
    return <span>{list.join(", ")}</span>;
  }

  return (
    <div className="field-app-telemetry-schedule__machines">
      {list.map((ident, index) => {
        const meta = operationMap[String(ident).trim()] || {};
        const operationId = meta.monitoringOperationId;
        const key = `${dayKey}-${ident}-${index}`;
        if (!operationId) {
          return (
            <span
              key={key}
              className="field-app-telemetry-schedule__machine-label"
            >
              {ident}
            </span>
          );
        }
        return (
          <Link
            key={key}
            className="field-app-telemetry-schedule__machine-link"
            to={`/${farmId}/operation/${operationId}${buildOperationReturnSearch(
              farmId,
              dayKey
            )}`}
            title={t("Abrir mapa e operações desta máquina")}
          >
            {ident}
          </Link>
        );
      })}
    </div>
  );
}

function TelemetryScheduleTable({
  rows,
  timezoneMode,
  onOpenMap,
  operationMap,
  farmId,
  t,
  printable,
}) {
  return (
    <table className="field-app-telemetry-schedule__table">
      <thead>
        <tr>
          <th>{t("Dia")}</th>
          <th>{t("Início")}</th>
          <th>{t("Pausa")}</th>
          <th>{t("Retorno")}</th>
          <th>
            <span className="field-app-telemetry-schedule__screen-label">
              {t("Encerramento")}
            </span>
            <span className="field-app-telemetry-schedule__compact-label">
              {t("Encerr.")}
            </span>
          </th>
          <th>
            <span className="field-app-telemetry-schedule__screen-label">
              {t("Horas manhã")}
            </span>
            <span className="field-app-telemetry-schedule__compact-label">
              {t("H. manhã")}
            </span>
          </th>
          <th>
            <span className="field-app-telemetry-schedule__screen-label">
              {t("Horas tarde")}
            </span>
            <span className="field-app-telemetry-schedule__compact-label">
              {t("H. tarde")}
            </span>
          </th>
          <th>{t("Total horas")}</th>
          <th>
            <span className="field-app-telemetry-schedule__screen-label">
              {t("Min. parado")}
            </span>
            <span className="field-app-telemetry-schedule__compact-label">
              {t("Parado")}
            </span>
          </th>
          <th>
            <span className="field-app-telemetry-schedule__screen-label">
              {t("% parado")}
            </span>
            <span className="field-app-telemetry-schedule__compact-label">%</span>
          </th>
          <th>
            <span className="field-app-telemetry-schedule__screen-label">
              {t("Vel. média")}
            </span>
            <span className="field-app-telemetry-schedule__compact-label">
              {t("V. méd.")}
            </span>
          </th>
          <th>
            <span className="field-app-telemetry-schedule__screen-label">
              {t("Vel. máxima")}
            </span>
            <span className="field-app-telemetry-schedule__compact-label">
              {t("V. máx.")}
            </span>
          </th>
          <th>{t("Máquinas")}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const hasData =
            row.startedAt ||
            row.pausedAt ||
            row.resumedAt ||
            row.finishedAt ||
            (Number(row.totalHours) || 0) > 0;

          if (!hasData) {
            return (
              <tr key={row.date} className="field-app-telemetry-schedule__empty-row">
                <td className="field-app-telemetry-schedule__date">
                  {formatDayLabel(row.date, printable)}
                </td>
                <td colSpan={12}>{t("Sem telemetria neste dia")}</td>
              </tr>
            );
          }

          const splitDay = (Number(row.afternoonHours) || 0) > 0;

          return (
            <tr key={row.date}>
              <td className="field-app-telemetry-schedule__date">
                {formatDayLabel(row.date, printable)}
              </td>
              <td>
                <ScheduleTimeCell
                  cell={row.startedAt}
                  dayKey={row.date}
                  label={t("Início")}
                  timezoneMode={timezoneMode}
                  onOpenMap={onOpenMap}
                  printable={printable}
                  t={t}
                />
              </td>
              <td>
                <ScheduleTimeCell
                  cell={row.pausedAt}
                  dayKey={row.date}
                  label={t("Pausa")}
                  timezoneMode={timezoneMode}
                  onOpenMap={onOpenMap}
                  printable={printable}
                  t={t}
                />
              </td>
              <td>
                <ScheduleTimeCell
                  cell={row.resumedAt}
                  dayKey={row.date}
                  label={t("Retorno")}
                  timezoneMode={timezoneMode}
                  onOpenMap={onOpenMap}
                  printable={printable}
                  t={t}
                />
              </td>
              <td>
                <ScheduleTimeCell
                  cell={row.finishedAt}
                  dayKey={row.date}
                  label={t("Encerramento")}
                  timezoneMode={timezoneMode}
                  onOpenMap={onOpenMap}
                  printable={printable}
                  t={t}
                />
              </td>
              <td className="field-app-telemetry-schedule__totals">
                {splitDay ? formatHours(row.morningHours) : formatHours(row.totalHours)}
              </td>
              <td className="field-app-telemetry-schedule__totals">
                {splitDay ? formatHours(row.afternoonHours) : "—"}
              </td>
              <td className="field-app-telemetry-schedule__totals">
                {formatHours(row.totalHours)}
              </td>
              <td>{formatStoppedMinutes(row.stoppedMinutes)}</td>
              <td>{formatStoppedPercent(row.stoppedPercent)}</td>
              <td>{formatSpeed(row.avgSpeedKmh)}</td>
              <td>
                <MaxSpeedCell
                  row={row}
                  onOpenMap={onOpenMap}
                  printable={printable}
                  t={t}
                />
              </td>
              <td>
                <DayMachinesCell
                  machines={row.machines}
                  operationMap={operationMap}
                  farmId={farmId}
                  dayKey={row.date}
                  printable={printable}
                  t={t}
                />
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function FieldAppTelemetrySchedulePanel({
  rows,
  loading,
  timezoneMode,
  farmId,
  selection,
  machines,
  operatorLabel,
  periodLabel,
  machineLabel,
  t,
}) {
  const [mapView, setMapView] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const printAreaRef = useRef(null);
  const list = Array.isArray(rows) ? rows : [];
  const operationMap = useMemo(
    () => buildMachineOperationMap(machines),
    [machines]
  );

  const handleOpenMap = useCallback(
    (payload) => openMapView(setMapView, payload),
    []
  );

  const handlePrint = useCallback(() => {
    printTelemetrySchedule();
  }, []);

  const handleExportPdf = useCallback(async () => {
    if (!printAreaRef.current || exportingPdf) return;
    setExportingPdf(true);
    try {
      await exportTelemetrySchedulePdf(
        printAreaRef.current,
        buildTelemetryScheduleFileName({ operatorLabel, periodLabel })
      );
    } finally {
      setExportingPdf(false);
    }
  }, [exportingPdf, operatorLabel, periodLabel]);

  if (loading) {
    return (
      <div className="field-app-telemetry-schedule">
        <p className="field-app-telemetry-schedule__hint">{t("Carregando planilha…")}</p>
      </div>
    );
  }

  if (!list.length) {
    return (
      <div className="field-app-telemetry-schedule">
        <p className="field-app-telemetry-schedule__hint">
          {t("Nenhum registro de telemetria no período selecionado.")}
        </p>
      </div>
    );
  }

  return (
    <div className="field-app-telemetry-schedule">
      <div className="field-app-telemetry-schedule__toolbar">
        <div className="field-app-telemetry-schedule__toolbar-group">
          <button
            type="button"
            className="btn btn-default field-app-telemetry-schedule__toolbar-btn"
            onClick={handlePrint}
            aria-label={t("Imprimir planilha em A4 paisagem")}
            title={t("Imprimir planilha em A4 paisagem")}
          >
            <FaPrint aria-hidden="true" />
          </button>
          <button
            type="button"
            className="btn btn-default field-app-telemetry-schedule__toolbar-btn"
            onClick={handleExportPdf}
            disabled={exportingPdf}
            aria-label={t("Salvar planilha em PDF (A4 paisagem)")}
            title={
              exportingPdf
                ? t("Gerando PDF…")
                : t("Salvar planilha em PDF (A4 paisagem)")
            }
          >
            <FaFileDownload aria-hidden="true" />
          </button>
        </div>
      </div>

      <p className="field-app-telemetry-schedule__hint field-app-telemetry-schedule__screen-only">
        {t(
          "Clique em um horário ou na velocidade máxima para abrir o mapa flutuante. Clique em uma máquina para abrir o mapa de operações. Paradas contam a partir de 5 minutos parado."
        )}
      </p>

      <div
        ref={printAreaRef}
        className="field-app-telemetry-schedule__print-area"
      >
        <div className="field-app-telemetry-schedule__print-header">
          <h2 className="field-app-telemetry-schedule__print-title">
            {t("Planilha de telemetria")}
          </h2>
          <div className="field-app-telemetry-schedule__print-meta">
            {operatorLabel ? (
              <span>
                <strong>{t("Operador")}:</strong> {operatorLabel}
              </span>
            ) : null}
            {periodLabel ? (
              <span>
                <strong>{t("Período")}:</strong> {periodLabel}
              </span>
            ) : null}
            {machineLabel ? (
              <span>
                <strong>{t("Máquina")}:</strong> {machineLabel}
              </span>
            ) : null}
            <span>
              <strong>{t("Gerado em")}:</strong> {formatReportGeneratedAt()}
            </span>
          </div>
        </div>

        <div className="field-app-telemetry-schedule__wrap">
          <TelemetryScheduleTable
            rows={list}
            timezoneMode={timezoneMode}
            onOpenMap={handleOpenMap}
            operationMap={operationMap}
            farmId={farmId}
            t={t}
            printable={false}
          />
        </div>
      </div>

      {mapView ? (
        <FieldAppTelemetryMapPanel
          farmId={farmId}
          selection={selection}
          view={mapView}
          onClose={() => setMapView(null)}
          t={t}
        />
      ) : null}
    </div>
  );
}

export default FieldAppTelemetrySchedulePanel;
